import React, { useEffect, useState } from 'react';
import { Music2 } from 'lucide-react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

// Gêneros e tagline são o texto literal de fragment_music_list.xml. A faixa
// "Floating Vibes" / "Luma" é o exemplo real usado em activity_music_player.xml.
const GENRES = [
  'Coral Infantil',
  'Clássico Suave',
  'Pop Alegre',
  'Natureza Acústico',
  'Eletrônico Calmo',
  'Lo-fi',
  'Jazz',
  'Rock Leve',
];
const TRACK_TITLE = 'Floating Vibes';
const TRACK_ARTIST = 'Luma';

const GENRE_INTERVAL = 1500;
const BAR_INTERVAL = 380;
const BAR_COUNT = 5;

function randomWalk(value: number, amplitude: number, min: number, max: number) {
  const next = value + (Math.random() - 0.5) * 2 * amplitude;
  return Math.max(min, Math.min(max, Math.round(next)));
}

/** Player com os gêneros reais do app em destaque rotativo, equalizador e barra de progresso simulando uma faixa gerada por IA tocando. */
export function MusicLiveDemo() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [genreIndex, setGenreIndex] = useState(1);
  const [bars, setBars] = useState<number[]>([45, 75, 55, 90, 60]);
  const [progress, setProgress] = useState(15);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const timer = setInterval(() => setGenreIndex((i) => (i + 1) % GENRES.length), GENRE_INTERVAL);
    return () => clearInterval(timer);
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const timer = setInterval(() => {
      setBars((b) => b.map((v) => randomWalk(v, 22, 25, 100)));
      setProgress((p) => (p >= 96 ? 4 : p + 4));
    }, BAR_INTERVAL);
    return () => clearInterval(timer);
  }, [prefersReducedMotion]);

  const shownBars = prefersReducedMotion ? [45, 75, 55, 90, 60] : bars;
  const shownProgress = prefersReducedMotion ? 45 : progress;

  return (
    <div className="rounded-2xl bg-card shadow-lg overflow-hidden p-4" aria-hidden="true">
      <p className="text-[11px] text-muted-foreground text-center leading-snug mb-3">
        Gere músicas com IA para relaxar e divertir!!
      </p>

      {/* Gêneros reais do app — um em destaque por vez */}
      <div className="flex flex-wrap justify-center gap-1.5 mb-4">
        {GENRES.map((genre, i) => (
          <span
            key={genre}
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors duration-500 ${
              i === genreIndex ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
            }`}
          >
            {genre}
          </span>
        ))}
      </div>

      {/* Player — "Floating Vibes" / "Luma", exemplo real de activity_music_player.xml */}
      <div className="rounded-2xl bg-gradient-to-br from-primary/15 to-warm/10 p-3.5 flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center text-white flex-shrink-0 shadow-md animate-pulse">
          <Music2 className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-foreground truncate">{TRACK_TITLE}</p>
          <p className="text-xs text-muted-foreground truncate">{TRACK_ARTIST}</p>
          <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${shownProgress}%` }}
            />
          </div>
        </div>
        <div className="flex items-end gap-0.5 h-8 flex-shrink-0">
          {shownBars.map((h, i) => (
            <span
              key={i}
              className="w-1 rounded-full bg-primary transition-all duration-300"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
